import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDarkMode } from '../context/DarkModeContext';
import Footer from '../components/Footer';
import '../styles/PasswordGeneratorPage.css';

const UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const LOWER = 'abcdefghijklmnopqrstuvwxyz';
const NUMBERS = '0123456789';
const SPECIAL = '!@#$%^&*()_+-=[]{}|;:,.<>?';

function generatePassword(length, useUpper, useLower, useNumbers, useSpecial) {
  // If all unchecked, treat all as checked
  const allOff = !useUpper && !useLower && !useNumbers && !useSpecial;
  const u = allOff || useUpper;
  const l = allOff || useLower;
  const n = allOff || useNumbers;
  const s = allOff || useSpecial;

  let charset = '';
  if (u) charset += UPPER;
  if (l) charset += LOWER;
  if (n) charset += NUMBERS;
  if (s) charset += SPECIAL;

  if (!charset) charset = LOWER;

  const arr = new Array(length);

  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const randomBytes = new Uint32Array(length);
    crypto.getRandomValues(randomBytes);
    for (let i = 0; i < length; i++) {
      arr[i] = charset[randomBytes[i] % charset.length];
    }
  } else {
    for (let i = 0; i < length; i++) {
      arr[i] = charset[Math.floor(Math.random() * charset.length)];
    }
  }

  // Fisher-Yates shuffle
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }

  return arr.join('');
}

function getStrength(password, useUpper, useLower, useNumbers, useSpecial) {
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (password.length >= 16) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  // Bonus for having many char types requested
  const types = [useUpper, useLower, useNumbers, useSpecial].filter(Boolean).length;
  if (types === 4) score++;

  if (score <= 2) return { label: 'Weak', level: 'weak', pct: 25 };
  if (score <= 4) return { label: 'Fair', level: 'fair', pct: 50 };
  if (score <= 6) return { label: 'Strong', level: 'strong', pct: 75 };
  return { label: 'Very Strong', level: 'very-strong', pct: 100 };
}

export default function PasswordGeneratorPage() {
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const [length, setLength] = useState(16);
  const [useUpper, setUseUpper] = useState(true);
  const [useLower, setUseLower] = useState(true);
  const [useNumbers, setUseNumbers] = useState(true);
  const [useSpecial, setUseSpecial] = useState(true);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(true);
  const [copied, setCopied] = useState(false);

  const regen = useCallback(() => {
    setPassword(generatePassword(length, useUpper, useLower, useNumbers, useSpecial));
    setCopied(false);
  }, [length, useUpper, useLower, useNumbers, useSpecial]);

  useEffect(() => {
    regen();
  }, [regen]);

  const handleCopy = () => {
    navigator.clipboard.writeText(password).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const strength = getStrength(password, useUpper, useLower, useNumbers, useSpecial);

  return (
    <div
      className={`container ${darkMode ? 'dark-theme' : ''}`}
      style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', padding: '20px' }}
    >
      {/* Dark mode toggle */}
      <button
        onClick={toggleDarkMode}
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 1000,
          color: '#fff',
          fontSize: '1rem',
          padding: '8px 16px',
          borderRadius: '6px',
          backgroundColor: '#1976d2',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1565c0'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1976d2'}
      >
        {darkMode ? '☀️ Light Mode' : '🌙 Dark Mode'}
      </button>

      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 3, gap: { xs: 2, sm: 0 } }}>
        <img
          src="/favicon.png"
          alt="Home"
          onClick={() => navigate('/')}
          title="Go to Home"
          style={{ height: '60px', width: '60px', cursor: 'pointer', borderRadius: '8px', flexShrink: 0, transition: 'opacity 0.15s' }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.75'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: darkMode ? '#42a5f5' : '#1976d2', textAlign: { xs: 'left', sm: 'center' } }}>
          🔐 Password Generator
        </Typography>
        <Box sx={{ width: { sm: '100px' } }} />
      </Box>

      {/* Card */}
      <div className="password-card">

        {/* Password display */}
        <div className="password-display">
          <span className="password-text">
            {showPassword ? password : '•'.repeat(password.length)}
          </span>
          <div className="password-display-actions">
            <button
              className="pwd-icon-btn"
              title={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword((v) => !v)}
            >
              {showPassword ? '🙈' : '👁️'}
            </button>
            <button
              className={`pwd-icon-btn${copied ? ' copied' : ''}`}
              title="Copy password"
              onClick={handleCopy}
            >
              {copied ? '✓' : '📋'}
            </button>
          </div>
        </div>

        {/* Strength bar */}
        <div className="strength-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span className="strength-label-text">Strength</span>
            <span className={`strength-label strength-${strength.level}`}>{strength.label}</span>
          </div>
          <div className="strength-bar-track">
            <div
              className={`strength-bar-fill strength-${strength.level}`}
              style={{ width: `${strength.pct}%` }}
            />
          </div>
        </div>

        {/* Length slider */}
        <div className="length-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span className="option-label">Length</span>
            <span className="length-value">{length}</span>
          </div>
          <input
            type="range"
            className="length-slider"
            min={8}
            max={64}
            value={length}
            onChange={(e) => setLength(Number(e.target.value))}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#90a4ae' }}>
            <span>8</span>
            <span>64</span>
          </div>
        </div>

        {/* Options */}
        <div className="options-section">
          {[
            { icon: '🔠', label: 'Uppercase A-Z', value: useUpper, setter: setUseUpper },
            { icon: '🔡', label: 'Lowercase a-z', value: useLower, setter: setUseLower },
            { icon: '🔢', label: 'Numbers 0-9', value: useNumbers, setter: setUseNumbers },
            { icon: '!@#', label: 'Special chars', value: useSpecial, setter: setUseSpecial },
          ].map(({ icon, label, value, setter }) => (
            <label key={label} className="option-card">
              <span className="option-icon">{icon}</span>
              <span className="option-label">{label}</span>
              <input
                type="checkbox"
                className="option-checkbox"
                checked={value}
                onChange={(e) => setter(e.target.checked)}
              />
              <span className="option-toggle" />
            </label>
          ))}
        </div>

        {/* Note */}
        <p className="pwd-note">
          If no options selected, a fully secure password is generated using all character types.
        </p>

        {/* Action buttons */}
        <div className="pwd-actions">
          <button className="pwd-btn-primary" onClick={regen}>
            🔄 Regenerate
          </button>
          <button className={`pwd-btn-outlined${copied ? ' copied' : ''}`} onClick={handleCopy}>
            📋 {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
}
